import { useEffect, useRef, useState } from 'react';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { FormField } from './FormField.tsx';
import { Select } from './Select.tsx';

interface OrgAttachment {
  id: string;
  organizationId: string;
  kind: 'agreement' | 'nda' | 'other';
  filename: string;
  mimeType: string;
  byteSize: number;
  signedOn: string | null;
  label: string | null;
  uploadedByAgentId: string | null;
  uploadedByAgentName: string | null;
  source: 'manual' | 'qwilr_webhook';
  qwilrDocId: string | null;
  createdAt: string;
}

interface Props {
  orgId: string;
}

const KIND_LABEL: Record<OrgAttachment['kind'], string> = {
  agreement: 'Agreement',
  nda: 'NDA',
  other: 'Other',
};

const KIND_PILL: Record<OrgAttachment['kind'], string> = {
  agreement: 'bg-[var(--color-t3-blue)]/10 text-[var(--color-t3-blue)]',
  nda: 'bg-[var(--color-t3-purple)]/10 text-[var(--color-t3-purple)]',
  other: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function OrgAttachmentsPanel({ orgId }: Props): React.ReactElement {
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const [items, setItems] = useState<OrgAttachment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadKind, setUploadKind] = useState<OrgAttachment['kind']>('agreement');
  const [uploadSignedOn, setUploadSignedOn] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function load() {
    try {
      const r = await apiFetch<{ attachments: OrgAttachment[] }>(
        `/api/organizations/${orgId}/attachments`,
      );
      setItems(r.attachments);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load attachments.');
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  async function remove(a: OrgAttachment) {
    if (!window.confirm(`Delete "${a.filename}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/organizations/${orgId}/attachments/${a.id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  async function upload(e: React.FormEvent) {
    e.preventDefault();
    setUploadError(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setUploadError('Pick a file to upload.');
      return;
    }
    const fd = new FormData();
    fd.set('file', file, file.name);
    fd.set('kind', uploadKind);
    if (uploadSignedOn) fd.set('signedOn', uploadSignedOn);
    if (uploadLabel.trim()) fd.set('label', uploadLabel.trim());
    setUploadBusy(true);
    try {
      await apiFetch(`/api/organizations/${orgId}/attachments`, { method: 'POST', body: fd });
      setUploadOpen(false);
      setUploadKind('agreement');
      setUploadSignedOn('');
      setUploadLabel('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e) {
      setUploadError(e instanceof ApiError ? e.message : 'Upload failed.');
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-[var(--color-header)]">
          Signed agreements & docs
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--color-muted)]">
            Auto-uploaded by the Qwilr webhook on contract signing.
          </span>
          {isAdmin && !uploadOpen && (
            <button
              type="button"
              onClick={() => {
                setUploadError(null);
                setUploadOpen(true);
              }}
              className="inline-flex items-center gap-1 text-sm text-[var(--color-t3-blue)] hover:underline"
            >
              <Upload className="h-3.5 w-3.5" /> Upload
            </button>
          )}
        </div>
      </div>
      {isAdmin && uploadOpen && (
        <form onSubmit={upload} className="mt-3 space-y-3 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
          <p className="text-xs text-[var(--color-muted)]">
            Manual upload — use when bypassing Qwilr (counter-signed offline, third-party NDA, etc.).
            Recorded in the audit log as <code>organization.attachment_uploaded</code>.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Select
              label="Kind"
              value={uploadKind}
              onChange={(e) => setUploadKind(e.target.value as OrgAttachment['kind'])}
            >
              <option value="agreement">Agreement</option>
              <option value="nda">NDA</option>
              <option value="other">Other</option>
            </Select>
            <FormField
              label="Signed on"
              name="signedOn"
              type="date"
              value={uploadSignedOn}
              onChange={(e) => setUploadSignedOn(e.target.value)}
              hint="Optional. The date the document was executed."
            />
            <FormField
              label="Label"
              name="label"
              value={uploadLabel}
              onChange={(e) => setUploadLabel(e.target.value)}
              placeholder="e.g. 2026 MSA Amendment"
              hint="Optional. Shown next to the filename."
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[var(--color-muted)]">
              File
            </label>
            <input
              ref={fileRef}
              type="file"
              required
              className="mt-1 block w-full text-sm"
            />
            <p className="mt-1 text-xs text-[var(--color-muted)]">PDFs and Word docs are typical. Max 25 MB.</p>
          </div>
          {uploadError && <p className="text-sm text-[var(--color-danger)]">{uploadError}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={uploadBusy}>
              {uploadBusy ? 'Uploading…' : 'Upload document'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                setUploadError(null);
              }}
              disabled={uploadBusy}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
      {error && <p className="mt-2 text-sm text-[var(--color-danger)]">{error}</p>}
      {items === null ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          No documents on file yet. They'll appear here automatically when the client signs a Qwilr
          agreement.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--color-border)]">
          {items.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-2.5">
              <FileText className="h-4 w-4 shrink-0 text-[var(--color-muted)]" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${KIND_PILL[a.kind]}`}>
                    {KIND_LABEL[a.kind]}
                  </span>
                  <span className="truncate font-medium text-[var(--color-header)]">{a.filename}</span>
                  {a.label && (
                    <span className="text-xs text-[var(--color-muted)]">· {a.label}</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-[var(--color-muted)]">
                  {a.signedOn ? `Signed ${a.signedOn} · ` : ''}
                  {formatBytes(a.byteSize)} ·{' '}
                  {a.source === 'qwilr_webhook' ? 'from Qwilr' : 'manual'}
                  {a.uploadedByAgentName ? ` · by ${a.uploadedByAgentName}` : ''}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={`/api/organizations/${orgId}/attachments/${a.id}/download`}
                  className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-xs font-semibold text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => void remove(a)}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-danger)] hover:underline"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
